import React, { useEffect, useState, useMemo } from 'react';
import LogoDark from '../../assets/images/logo/logo-dark.svg';
import { SidebarContainer, NavItem, LogoImage, SidebarHeader, Overlay, SidebarItems, NotificationDot } from './sidebar.styles';
import { INVESTOR_MENU_ITEMS, TENANT_MENU_ITEMS, IMenuItem } from '../../config/menuConfig';
import { colorPalette } from '../../utils/theme';
import MaterialIcon from '../material-icon';
import { useSwipeable } from 'react-swipeable';
import useCustomNavigate from '../../hooks/useCustomNavigate';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useInvestorDetailStore from '../../stores/investorDetailStore';
import useTicketSystemEnabled from '../../hooks/useTicketSystemEnabled';
import useTenantDetailStore from '../../stores/tenantDetailStore';
import { DocumentStatus, DocumentType } from '../../services/documentService';

interface SidebarProps {
    isSidebarVisible: boolean;
    toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isSidebarVisible, toggleSidebar }) => {
    const navigate = useCustomNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    // Stores
    const { investor } = useInvestorDetailStore();
    const ticketsEnabled = useTicketSystemEnabled();
    const { tenant } = useTenantDetailStore();

    // States
    const [activeItem, setActiveItem] = useState<string>('Dashboard');
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const [menuItems, setMenuItems] = useState<IMenuItem[]>(investor ? INVESTOR_MENU_ITEMS : tenant ? TENANT_MENU_ITEMS : []);

    // Effects
    useEffect(() => {
        const findActiveItem = () => {
            const active = menuItems.find((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`));
            return active ? active.name : '';
        };
        setActiveItem(findActiveItem());
    }, [menuItems, location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (isMobile) {
            // Show "Account" menu item on mobile
            setMenuItems(prevItems =>
                prevItems.map(item =>
                    item.name === "Account" ? { ...item, isVisible: true } : item
                )
            );
        } else {
            // Hide "Account" on larger screens
            setMenuItems(prevItems =>
                prevItems.map(item =>
                    item.name === "Account" ? { ...item, isVisible: false } : item
                )
            );
        }
    }, [isMobile]);

    const handleItemClick = (item: IMenuItem) => {
        if (!item.isActivated) return;
        if (activeItem !== item.name) {
            setActiveItem(item.name);
            toggleSidebar();
            navigate(item.path, { replace: true, clearHistory: true });
        }
    };

    // Übersetze den Menüeintrag basierend auf dem Namen
    const getTranslatedMenuName = (name: string) => {
        switch (name) {
            case "Dashboard":
                return t('tab_bar.landlord.dashboard');
            case "Meine Einheiten":
                return t('tab_bar.landlord.units');
            case "Finanzen":
                return t('tab_bar.landlord.finances');
            case "Verträge":
                return t('tab_bar.landlord.contracts');
            case "Support":
                return t('tab_bar.landlord.requests');
            case "Knowledge Hub":
                return t('tab_bar.landlord.knowledge_hub');
            case "Account":
                return t('tab_bar.account');
            default:
                return name;
        }
    };

    // Investor Account Memos (same logic as topbar)
    const hasAddress = useMemo(() => {
        if (!investor) return false;
        if (tenant) return true;
        return investor.address.country !== null || investor.address.country !== undefined;
    }, [investor, tenant]);

    const expiredDocuments = useMemo(() => {
        if (investor) {
            return investor.documents.filter(doc => {
                return doc.valid_until !== null && new Date(doc.valid_until) < new Date();
            });
        } else if (tenant) {
            return tenant.documents.filter(doc => {
                return doc.valid_until !== null && new Date(doc.valid_until) < new Date();
            });
        } else {
            return [];
        }
    }, [investor, tenant]);

    const declinedDocuments = useMemo(() => {
        if (investor) {
            return investor.documents.filter(doc => {
                return doc.status === DocumentStatus.DECLINED;
            });
        } else if (tenant) {
            return tenant.documents.filter(doc => {
                return doc.status === DocumentStatus.DECLINED;
            });
        } else {
            return [];
        }
    }, [investor, tenant]);

    const inReviewDocuments = useMemo(() => {
        if (investor) {
            return investor.documents.filter(doc => {
                return doc.status === DocumentStatus.IN_REVIEW;
            });
        } else if (tenant) {
            return tenant.documents.filter(doc => {
                return doc.status === DocumentStatus.IN_REVIEW;
            });
        } else {
            return [];
        }
    }, [investor, tenant]);

    const hasMandatoryDocumentsUploaded = useMemo(() => {
        if (investor) {
            return investor.documents.filter(document => document.document_type === DocumentType.PASSPORT || document.document_type === DocumentType.PROOF_OF_ADDRESS).length > 1
        } else if (tenant) {
            return tenant.documents.filter(document => document.document_type === DocumentType.PASSPORT || document.document_type === DocumentType.EMIRATES_ID).length > 1
        } else {
            return false;
        }
    }, [investor, tenant]);

    // Header States
    const headerStatus = useMemo(() => {
        if (!hasAddress && !hasMandatoryDocumentsUploaded) return "inactive";
        if (!hasMandatoryDocumentsUploaded || expiredDocuments.length > 0 || declinedDocuments.length > 0) return "inactive";
        if (inReviewDocuments.length > 0) return "in_review";
        return "active";
    }, [expiredDocuments, hasAddress, hasMandatoryDocumentsUploaded, declinedDocuments, inReviewDocuments]);

    // Swipeable configuration for mobile
    const handlers = useSwipeable({
        onSwipedLeft: () => toggleSidebar(),
        onSwipedRight: () => toggleSidebar(),
        delta: 30,
        trackMouse: true,
    });

    return (
        <>
            <SidebarContainer className="d-lg-none" isVisible={isSidebarVisible} {...handlers}>
                <SidebarHeader>
                    <LogoImage src={LogoDark} alt="Logo" onClick={() => navigate("/", { replace: true, clearHistory: true })} />
                    <MaterialIcon
                        className="d-lg-none d-block"
                        icon="close"
                        size={24}
                        color={colorPalette.textMain}
                        onClick={toggleSidebar}
                    />
                </SidebarHeader>
                <SidebarItems className="nav flex-column">
                    {menuItems.filter(item => item.name !== "Account").map((item) => {
                        const isActive = activeItem === item.name;
                        if (!item.isVisible || (item.requiresTicketSystem && !ticketsEnabled)) return null;

                        return (
                            <NavItem key={item.id} isActive={isActive} isDeactivated={!item.isActivated} onClick={() => handleItemClick(item)}>
                                <button
                                    className={`nav-link ${!item.isActivated ? 'deactivated' : ''}`}
                                    disabled={!item.isActivated}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <MaterialIcon icon={item.icon} size={20} color={isActive ? colorPalette.blue500 : colorPalette.primaryDark} />
                                        {getTranslatedMenuName(item.name)}
                                    </div>
                                    <MaterialIcon icon="chevron_forward" size={20} color={colorPalette.blue500} />
                                </button>
                            </NavItem>
                        );
                    })}
                </SidebarItems>

                <SidebarItems className="nav flex-column">
                    {menuItems.filter(item => item.name === "Account").map((item) => {
                        const isActive = activeItem === item.name;
                        if (!item.isVisible) return null;

                        return (
                            <NavItem key={item.id} isActive={isActive} isDeactivated={!item.isActivated} onClick={() => handleItemClick(item)}>
                                <button
                                    className={`nav-link ${!item.isActivated ? 'deactivated' : ''}`}
                                    disabled={!item.isActivated}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{ position: 'relative', display: 'inline-flex' }}>
                                            <MaterialIcon icon={item.icon} size={20} color={isActive ? colorPalette.blue500 : colorPalette.primaryDark} />
                                            {headerStatus === "inactive" && <NotificationDot />}
                                        </div>
                                        {getTranslatedMenuName(item.name)}
                                    </div>
                                    <MaterialIcon icon="chevron_forward" size={20} color={colorPalette.blue500} />
                                </button>
                            </NavItem>
                        );
                    })}
                </SidebarItems>
            </SidebarContainer>
            {isSidebarVisible && <Overlay onClick={toggleSidebar} />}
        </>
    );
};

export default Sidebar;
